import React, { useReducer, useCallback, useContext, useState, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Form, Button, Dropdown, Container } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormErrorState, FormErrorAction } from 'types/validationError.types.ts';
import {
  validateEmail, validateCompanyName, validateFirstName,
  validateLastName, validateDob, validatePhoneNumber,
  validateStreet, validateZip, validateCity,
  validateIBAN, validateAccountHolder, validateDebitForm,
} from '../validationFunctions/inputValidations.tsx';
import { v4 as uuidv4 } from 'uuid';

// Import assets
import paymentOptions from '../mockdata/paymentOptions.json';
import { UUIDContext, PBLResponseContext } from "../UUIDContexted";
import CONSTANTS from "../utils/constants";
import { submitDirectDebitPaymentRequest } from "../api/apiService.ts";
import fetchIpAddress from "../utils/fetchIP.js";
import { PaymentProcessingLoader } from './PaymentProcessingLoader.tsx';
import PaylaDcs from './PaylaDsc.tsx';
import { clearEmptyErrors, usePaymentForm } from "../context/PaymentFormContext";
import PaymentNotSupported from './PaymentNotSupported.tsx';

// Types
export interface FormState {
  customer: {
    businessRelation: 'Business to Customer' | 'Business to Business';
    companyName: string;
    retainedBusinessValue: string;
    firstName: string;
    lastName: string;
    dob: string;
    formattedDate: string;
  };
  contact: {
    email: string;
    phone: string;
  };
  billing: {
    address: string;
    zipcode: string;
    city: string;
    country: string;
  };
  payment: {
    bankAccountHolder: string;
    iban: string;
  };
  agreed: boolean;
}

type FormAction =
  | { type: 'UPDATE_CUSTOMER'; field: keyof FormState['customer']; value: string }
  | { type: 'UPDATE_CONTACT'; field: keyof FormState['contact']; value: string }
  | { type: 'UPDATE_BILLING'; field: keyof FormState['billing']; value: string }
  | { type: 'UPDATE_PAYMENT'; field: keyof FormState['payment']; value: string }
  | { type: 'SET_BUSINESS_RELATION'; value: 'Business to Customer' | 'Business to Business' }
  | { type: 'TOGGLE_AGREEMENT' }
  | { type: "SET_FORM_STATE"; payload: FormState }
  | { type: 'FORMAT_DOB'; value: string };


// Reusable components
const FormFieldGroup: React.FC<{
  id: string;
  label: string;
  testId?: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ id, label, testId, required = true, children }) => (
  <Form.Group controlId={id}>
    <Form.Label data-testid={testId}>
      {label} {required ? (<span style={{ color: '#F50057' }}>*</span>) : ""}
    </Form.Label>
    {children}
  </Form.Group>
);


// Custom dropdown component
const CustomDropdown: React.FC<{
  id: string;
  disable: boolean;
  value: string;
  options: { key: string; value: string }[];
  testId?: string;
  onSelect: (value: string) => void;
}> = ({ id, disable = false, value, options, testId, onSelect }) => (
  <Dropdown onSelect={(eventKey) => eventKey && onSelect(eventKey)} className="w-100 volte-medium">
    <Dropdown.Toggle disabled={disable}
      className={`w-100 d-flex justify-content-between align-items-center ${disable ? "disabled-dropdown" : ""}`}
      id={id}
      style={{
        backgroundColor: "white",
        borderColor: "#b1b6ba",
      }}
      data-testid={testId}
    >
      {value}
    </Dropdown.Toggle>
    <Dropdown.Menu className="w-100 rounded-3 form-dropdown">
      {options.map((option) => (
        <Dropdown.Item
          key={option.key}
          eventKey={option.key}
          data-testid={`${testId}-${option.key.toLowerCase()}`}
        >
          {option.value}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

//custom error message component
const FormFieldError: React.FC<{
  id: string;
  value: string;
  userState: string;
  testId: string;
}> = ({ id, value, userState, testId }) => (
  userState !== 'typing' && value !== '' &&
  <span id={id} data-testid={testId} className="error_message">
    <FormattedMessage id={value} />
  </span>
)

// Main component
const DirectDebit: React.FC = () => {

  const intl = useIntl();
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  
  const [isPaymentMethodAvailable, setIsPaymentMethodAvailable] = useState(false);
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [showPaymentProcessing, setShowPaymentProcessing] = useState(false);
  const [snippetToken, setSnippetToken] = useState("");
  // getting the data from the context
  const PBLData = useContext(UUIDContext);
  const { setPblResponseData } = useContext(PBLResponseContext);
  const { PaymentFormData, PaymentFormErrors, setPaymentFormData, setPaymentFormErrors } = usePaymentForm();
  const [overviewAttempted, setOverviewAttempted] = useState(false);
  const [isOverviewPage, setIsOverviewPage] = useState(false);

  const [isEditingCustomerInfo, setIsEditingCustomerInfo] = useState(false);
  const [isEditingContactInfo, setIsEditingContactInfo] = useState(false);
  const [isEditingBillingInfo, setIsEditingBillinfInfo] = useState(false);


  const [userInputState, setUserInputState] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    dob: '',
    phone: '',
    email: '',
    address: '',
    zip: '',
    city: '',
    iban: '',
    accountHolder: '',
  });
  
  // Initial state
  const initialState: FormState = {
    customer: {
      businessRelation: 'Business to Customer',
      companyName: '',
      retainedBusinessValue: '',
      firstName: '',
      lastName: '',
      dob: '',
      formattedDate: '',
    },
    contact: {
      email: '',
      phone: '',
    },
    billing: {
      address: '',
      zipcode: '',
      city: '',
      country: 'Germany',
    },
    payment: {
      bankAccountHolder: '',
      iban: '',
    },
    agreed: false,
  };

  // rendering the fields based on the API reseposne 
  const [fieldVisibility, setFieldVisibility] = useState({
    isCity: false,
    isStreet: false,
    isZip: false,
    isCountryCode: false,
    isBussiness: false,
    isCompanyName: false,
    isFirstName: false,
    isLastName: false,
    isDob: false,
    isEmail: false,
    isPhone: false,
    isBankHolder: true,
    isIban: true,
    isLabelForCustomer: false,
    isLabelForContact: false,
    isLabelForBilling: true,
  });

  function mapPaymentFormDataToFormState(data:any): FormState {
    return {
      customer: {
        businessRelation: 'Business to Customer',
        companyName: '',
        retainedBusinessValue: data.retainedBusinessValue || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        dob: data.dob || '',
        formattedDate: data.formattedDate || '',
      },
      contact: {
        email: data.email || '',
        phone: data.phone || '',
      },
      billing: {
        address: data.address || '',
        zipcode: data.zipcode || '',
        city: data.city || '',
        country: data.selectedCountry || '',
      },
      payment: {
        bankAccountHolder: data.bankaccholder || '',
        iban: data.iban || '',
      },
      agreed: data.agreed || false,
    };
  }

  //generate snippetToken only once
  useEffect(() => {
    if (PaymentFormData) {
      dispatch({
        type: "SET_FORM_STATE",
        payload: mapPaymentFormDataToFormState(PaymentFormData),
      });
    }

    const partnerMerchantId = PBLData ? PBLData.bnplId : '';
    setSnippetToken(`${CONSTANTS.PAYLA_PARTNER_ID}_${partnerMerchantId}_${uuidv4()}`);
    setPblResponseData(null);
  },[]);

  useEffect(() => {
    if (PBLData && PBLData.requiredParameters) {
      const filterPaymentMethod = PBLData?.requiredParameters.find(item => item['3392']);
      const dataOfDirectDebit =  filterPaymentMethod ? filterPaymentMethod['3392'] : null;

      const updatedfieldVisibility = { ...fieldVisibility };

      dataOfDirectDebit?.forEach((field) => {
        if (field === "customer.billingAddress.city") {
          updatedfieldVisibility.isCity = true;
          updatedfieldVisibility.isLabelForBilling = true;
        } else if (field === "customer.billingAddress.street") { // Billing Address
          updatedfieldVisibility.isStreet = true;
          updatedfieldVisibility.isLabelForBilling = true;
        } else if (field === "customer.billingAddress.zip") {
          updatedfieldVisibility.isZip = true;
          updatedfieldVisibility.isLabelForBilling = true;
        } else if (field === "customer.billingAddress.countryCode") {
          updatedfieldVisibility.isCountryCode = true;
          updatedfieldVisibility.isLabelForBilling = true;
        } else if (field === "customer.personalInformation.name.firstName") { // Personal Information Fields 
          updatedfieldVisibility.isFirstName = true;
          updatedfieldVisibility.isLabelForCustomer = true;
        } else if (field === "customer.personalInformation.name.surname") {
          updatedfieldVisibility.isLastName = true;
          updatedfieldVisibility.isLabelForCustomer = true;
        } else if (field === "customer.personalInformation.dateOfBirth") {
          updatedfieldVisibility.isDob = true;
          updatedfieldVisibility.isLabelForCustomer = true;
        } else if (field === "customer.contactDetails.emailAddress") { // Contact Details
          updatedfieldVisibility.isEmail = true;
          updatedfieldVisibility.isLabelForContact = true;
        } else if (field === "customer.contactDetails.phoneNumber") {
          updatedfieldVisibility.isPhone = true;
          updatedfieldVisibility.isLabelForContact = true;
        } else if (field === "customer.businessRelation") {
          updatedfieldVisibility.isBussiness = true;
          updatedfieldVisibility.isCompanyName = true;
          updatedfieldVisibility.isLabelForCustomer = true;
        }
      });

      setFieldVisibility(updatedfieldVisibility);
    }
    if (PBLData && PBLData.paymentMethods && PBLData.paymentMethods.length > 0) {
      let invoicePaymentMethodsID = paymentOptions.find(option => option.id === "debit")?.paymentMethodsID;
      if (invoicePaymentMethodsID) {
        let isPaymentMethodAvailable = PBLData.paymentMethods.includes(invoicePaymentMethodsID);
        setIsPaymentMethodAvailable(isPaymentMethodAvailable)
      }
    }
  }, [PBLData]);

  // Form reducer
  const formReducer = (state: FormState, action: FormAction): FormState => {
    
    switch (action.type) {
      
      case 'UPDATE_CUSTOMER':
        return {
          ...state,
          customer: {
            ...state.customer,
            [action.field]: action.value,
          },
        };
      case 'UPDATE_CONTACT':
        return {
          ...state,
          contact: {
            ...state.contact,
            [action.field]: action.value,
          },
        };
      case 'UPDATE_BILLING':
        return {
          ...state,
          billing: {
            ...state.billing,
            [action.field]: action.value,
          },
        };
      case 'UPDATE_PAYMENT':
        return {
          ...state,
          payment: {
            ...state.payment,
            [action.field]: action.value,
          },
        };
      case 'SET_BUSINESS_RELATION':
        // Handle business relation change logic
        if (action.value === 'Business to Customer' && state.customer.companyName) {
          return {
            ...state,
            customer: {
              ...state.customer,
              businessRelation: action.value,
              retainedBusinessValue: state.customer.companyName,
              companyName: '',
            },
          };
        } else if (action.value === 'Business to Business') {
          return {
            ...state,
            customer: {
              ...state.customer,
              businessRelation: action.value,
              companyName: state.customer.retainedBusinessValue,
            },
          };
        }
        return {
          ...state,
          customer: {
            ...state.customer,
            businessRelation: action.value,
          },
        };
      case 'TOGGLE_AGREEMENT':
        return {
          ...state,
          agreed: !state.agreed,
        };
      case "SET_FORM_STATE":
        return { ...action.payload };
      case 'FORMAT_DOB':
        const value = action.value;

        // Code to format DOB 
        let formattedDate = '';
        if (value.length <= 2) {
          formattedDate = value;
        } else if (value.length <= 4) {
          formattedDate = `${value.slice(0, 2)}.${value.slice(2)}`;
        } else {
          formattedDate = `${value.slice(0, 2)}.${value.slice(2, 4)}.${value.slice(4, 8)}`;
        }
        setPaymentFormData({
          ...PaymentFormData,
          dob: formattedDate
        })

        return {
          ...state,
          customer: {
            ...state.customer,
            dob: formattedDate,
            formattedDate,
          },
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(formReducer, initialState);

  const initialErrorState: FormErrorState = {
    customerError: {
      companyNameError: '',
      firstNameError: '',
      lastNameError: '',
      dobError: '',
    },
    contactError: {
      emailError: '',
      phoneError: '',
    },
    billingError: {
      addressError: '',
      zipcodeError: '',
      cityError: '',
      countryError:""
    },
    paymentError: {
      iban: '',
      bankAccountHolderError: '',
    },
    notAgreedError: ''
  };

  const formErrorReducer = (errorState: FormErrorState, action: FormErrorAction): FormErrorState => {
    switch (action.type) {
      case 'SET_CUSTOMER_ERROR':
        return {
          ...errorState,
          customerError: {
            ...errorState.customerError,
            [action.field]: action.value
          }
        };
      case 'SET_CONTACT_ERROR':
        return {
          ...errorState,
          contactError: {
            ...errorState.contactError,
            [action.field]: action.value
          }
        }
      case 'SET_BILLING_ERROR':
        return {
          ...errorState,
          billingError: {
            ...errorState.billingError,
            [action.field]: action.value
          }
        }
      case 'SET_PAYMENT_ERROR':
        return {
          ...errorState,
          paymentError: {
            ...errorState.paymentError,
            [action.field]: action.value
          }
        }
      case 'SET_NOT_AGREED_ERROR':
        return {
          ...errorState,
          notAgreedError: action.value
        }
      case "SET_ALL_ERROR":
        return {
          ...action.errors,
          notAgreedError: "",
        } as FormErrorState;
      default:
        return errorState;
    }
  }

  const [formErrorState, errorDispatch] = useReducer(formErrorReducer, initialErrorState);

  //this function recurses through formErrorState and search for non-empty strings (errors) and returns true if errors not present 
  const containsError = (errors: FormErrorState): boolean => {
    const check = (obj: any): boolean => {
      for (const key in obj) {
        if (typeof obj[key] === 'object') {
          if (check(obj[key])) {
            return true;
          }
        } else if (typeof obj[key] === 'string' && obj[key] !== '') {
          return true
        }
      }
      return false;
    }
    return check(errors)
  }

  const handleSubmit = useCallback(async () => {
    validateDebitForm(state, fieldVisibility, errorDispatch, false);

    setSubmissionAttempted(true);
  }, [state, formErrorState, fieldVisibility]);


  const handleOverviewPage = async () => {
    validateDebitForm(state, fieldVisibility, errorDispatch, true);
    setOverviewAttempted(true);
    window.scrollTo({top: 0, behavior: 'smooth'});
  }

  function convertDateFormat(dateStr: string): string {
    const parts = dateStr.split('.');
    if(parts && parts.length > 2){
      const [dd, mm, yyyy] = parts;
      return `${yyyy}${mm}${dd}`;  
    }
    return '';
  }

  useEffect(() => {
    async function makePostRequest() {
      if (submissionAttempted) {
        const hasError = containsError(formErrorState);

        if (!hasError) {
          setShowPaymentProcessing(true);
          setPblResponseData(null);
          try {
            const updatedFormData = {
              ...state,
              linkId: PBLData?.linkId,
              customer: {
                  ...state.customer,
                  businessRelation: fieldVisibility.isBussiness
                      ? (state.customer.businessRelation  == 'Business to Customer' ? 'B2C' : 'B2B')
                      : "",
                  dob: convertDateFormat(state.customer.dob),
              },
              contact: {
                ...state.contact,
                phone: state.contact.phone.replace(/\s/g, ''),
              },
              billing: {
                  ...state.billing,
                  country: fieldVisibility.isCountryCode
                      ? state.billing.country 
                      : "",
              },
          };
            const ipAddress = await fetchIpAddress();
            const response = await submitDirectDebitPaymentRequest(updatedFormData, ipAddress, snippetToken);
            const paymentStatus = response.createPaymentResponse.payment.status;
            if (
              paymentStatus === "PENDING_CAPTURE" ||
              paymentStatus === "CREATED" || paymentStatus === "CAPTURED"
            ) {              
              setPblResponseData({ ...response.createPaymentResponse, linkId: uuid });
              navigate(`/${uuid}/success/`);
            }
          } catch (error) {
            setPblResponseData({ error: error, linkId: uuid });
            setShowPaymentProcessing(false);
            // navigate(`/${uuid}/error/`);
          }
        }
      }
    }
    makePostRequest();

    return () => {
      setSubmissionAttempted(false);
    }
  }, [submissionAttempted]);

  const handleCompanyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch({ type: 'UPDATE_CUSTOMER', field: 'companyName', value });
    validateCompanyName(value, errorDispatch);
    
    if (state.customer.businessRelation === 'Business to Business') {
      dispatch({ type: 'UPDATE_CUSTOMER', field: 'retainedBusinessValue', value });
      validateCompanyName(value, errorDispatch);
    }
  }, [state.customer.businessRelation]);


  useEffect(() => {
    if (overviewAttempted) {
      const hasError = containsError(formErrorState);
      if (!hasError) {
        setIsOverviewPage(true);
        const updatedFormData = {
          ...state,
          selectedBusiness: state.customer.businessRelation ? state.customer.businessRelation : "Business to Customer", // Default value
          selectedCountry: state.billing.country ? state.billing.country : 'Germany', // Default value
          isOverviewPage: true,
          firstName: state.customer.firstName, // Ensure all required fields are included
          lastName: state.customer.lastName,
          dob: state.customer.dob,
          formattedDate: state.customer.formattedDate,
          phone: state.contact.phone, // Added required field
          email: state.contact.email, // Added required field
          companyName: state.customer.companyName, // Added required field
          address: state.billing.address, // Added required field
          // Add other required fields as necessary
          agreed: state.agreed, // Added required field
          iban: state.payment.iban,
          bankaccholder: state.payment.bankAccountHolder,
        };
       
        setPaymentFormData({
          ...updatedFormData,
          zipcode: state.billing.zipcode, // Ensure zipcode is included
          city: state.billing.city, // Ensure city is included
          retainedBusinessValue: state.customer.retainedBusinessValue || '', // Ensure retainedBusinessValue is included
          paymentMethod: '3392', // Ensure paymentMethod is included
          isOverviewPage: true
        });
      }
      setOverviewAttempted(false);
    }
  }, [formErrorState, overviewAttempted]);

  function isErrorFree(errorObj: any) {
    return Object.values(errorObj).every((val) => val === "");
  }

  const validateCustomerInfo = () => {
    if (isErrorFree(formErrorState.customerError)) {
      setIsEditingCustomerInfo(false);
    }
  };

  const validateContactInfo = () => {
    if (isErrorFree(formErrorState.contactError)) {
      setIsEditingContactInfo(false);
    }
  };

  const validateBillingInfo = () => {
    const canSave = isErrorFree(formErrorState.billingError);
    const isValidIbanAndHolder = isErrorFree(formErrorState.paymentError);
    if ( canSave && isValidIbanAndHolder) {
      setIsEditingBillinfInfo(false);
    } 
  };

  useEffect(() => {
    const { notAgreedError, ...errors } = formErrorState;
    const newErrors = clearEmptyErrors(errors);
    setPaymentFormErrors(newErrors);
  }, [formErrorState]);

  useEffect(() => {
    if (PaymentFormData && PaymentFormErrors) {
      errorDispatch({
        type: 'SET_ALL_ERROR',
        errors: PaymentFormErrors
      });
    }
  }, []);

  return (
    <>
      {isPaymentMethodAvailable ?
        <Container fluid className="d-flex flex-column g-0" data-testid="debit_payment_section">
          <Row className="flex-grow-1">
            <Col>
              <Form>
                {/* Customer Section */}
                {fieldVisibility.isLabelForCustomer && (
                  <div data-testid="payment_customer_info_section">
                    <Row className="flex-grow-1 mb-16">
                      <h5 className="section-title" data-testid="pm-text" style={{ display: isOverviewPage ? 'inline-flex' : 'block' }}>
                        <FormattedMessage
                          id={CONSTANTS.PAYMENT_FORM.PAGE_HEADING}
                          defaultMessage="Customer Information"
                        />
                        {!!isOverviewPage && <span className="text">
                            {!isEditingCustomerInfo ? <a href="#" onClick={(e)=>{
                            e.preventDefault();
                            setIsEditingCustomerInfo(true);
                            }} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}>
                            <FormattedMessage id={CONSTANTS.FORM_EDIT.EDIT_LINK_TEXT} defaultMessage="Edit"/></a> : <a href="#"
                             onClick={(e)=>{
                              e.preventDefault();
                              validateCustomerInfo();
                               }} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}>
                            <FormattedMessage id={CONSTANTS.FORM_EDIT.SAVE_BUTTON_TEXT} defaultMessage="Save"/></a>
                            }
                        </span>}
                      </h5>
                      {!isOverviewPage && <span className="section-subtitle volte-medium" data-testid="pm-text-customer-section">
                        <FormattedMessage
                          id={CONSTANTS.PAYMENT_FORM.PAGE_SUB_HEADING}
                          defaultMessage="Please enter your customer information"
                        />
                      </span>}
                    </Row>
                    <Row className="flex-grow-1">
                      {fieldVisibility.isBussiness && (
                        <Col xs={12} md={12} className="mb-24">
                          {isOverviewPage && !isEditingCustomerInfo ? <div ><label className="form-label" data-testid="pm-bussines-relation-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.BUSINESS_RELATION, defaultMessage: "Business relation" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >
                          {state.customer.businessRelation == "Business to Customer" ? intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.BUSINESS_RELATION.B2C, defaultMessage: "Business to Customer" })
                      : intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.BUSINESS_RELATION.B2B, defaultMessage: "Business to Business" })}
                            </span>  </div>:
                          <FormFieldGroup
                            id="businessRelation"
                            label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.BUSINESS_RELATION, defaultMessage: "Business Relation" })}
                            testId="pm-bussines-relation-label"
                            required={false}
                          >
                            <CustomDropdown
                              id="businessRelation"
                              disable={true}
                              value={
                                state.customer.businessRelation == "Business to Customer" ? intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.BUSINESS_RELATION.B2C, defaultMessage: "Business to Customer" })
                                  : intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.BUSINESS_RELATION.B2B, defaultMessage: "Business to Business" })
                                }
                              options={[
                                { key: 'Business to Customer', value: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.AUSTRIA, defaultMessage: "Austria" })}` },
                                { key: 'Business to Business', value: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.GERMANY, defaultMessage: "Germany" })}` }
                              ]}
                              testId="pm-bussines-relation-input"
                              onSelect={(value) =>
                                dispatch({
                                  type: 'SET_BUSINESS_RELATION',
                                  value: value as 'Business to Customer' | 'Business to Business'
                                })
                              }
                            />
                          </FormFieldGroup>}
                        </Col>
                      )}

                      {fieldVisibility.isCompanyName && state.customer.businessRelation === "Business to Business" && (
                        <Col xs={12} md={12} className="mb-24">
                          {isOverviewPage  && !isEditingCustomerInfo ? <div ><label className="form-label" data-testid="pm-companyName-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.COMPANY_NAME, defaultMessage: "Company name" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.customer.companyName}</span></div>:
                          <FormFieldGroup
                          id="companyName"
                          label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.COMPANY_NAME, defaultMessage: "Company Name" })}
                          testId="pm-companyName-label"
                          >
                            <Form.Control
                              type="text"
                              value={state.customer.companyName}
                              disabled={state.customer.businessRelation !== "Business to Business"}
                              onChange={handleCompanyChange}
                              onBlur={(e) => {
                                validateCompanyName(e.target.value, errorDispatch)
                                setUserInputState({...userInputState, companyName: ''})
                              }}
                              onFocus={() => setUserInputState({...userInputState, companyName: 'typing'})}
                              placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.COMPANY_NAME, defaultMessage: "Enter company name" })}
                              data-testid="pm-companyname-input"
                              className={state.customer.businessRelation === 'Business to Business' ? formErrorState.customerError.companyNameError ? 'error' : '' : ''}
                            />
                          {state.customer.businessRelation === 'Business to Business'
                            && <FormFieldError id='companyname-error' value={formErrorState.customerError.companyNameError} userState={userInputState.companyName} testId='pm-companyname-error' />}
                          </FormFieldGroup>}
                        </Col>
                      )}
                    </Row>

                    <Row className="flex-grow-1">
                      {fieldVisibility.isFirstName && (
                        <Col xs={12} md={6} className="mb-24">
                        {isOverviewPage && !isEditingCustomerInfo? <div ><label className="form-label" data-testid="pm-firstName-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.FIRST_NAME, defaultMessage: "First name" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.customer.firstName}</span></div>
                        :<FormFieldGroup
                          id="firstName"
                          label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.FIRST_NAME, defaultMessage: "First name" })}
                          testId="pm-firstName-label"
                          >
                          <Form.Control
                            type="text"
                            value={state.customer.firstName}
                            onChange={(e) => {
                              dispatch({
                                type: 'UPDATE_CUSTOMER',
                                field: 'firstName',
                                value: e.target.value
                              });
                              setPaymentFormData({
                                ...PaymentFormData,
                                firstName:e.target.value
                              })
                              validateFirstName(e.target.value, errorDispatch);
                            }}
                            onBlur={(e) => {
                              validateFirstName(e.target.value, errorDispatch)
                              setUserInputState({...userInputState, firstName: ''})
                            }}
                            onFocus={() => setUserInputState({...userInputState, firstName: 'typing'})}
                            placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.FIRST_NAME, defaultMessage: "Enter first name" })}
                            data-testid="pm-firstName-input"
                            className={formErrorState.customerError.firstNameError ? 'error' : ''}
                          />
                          <FormFieldError id='firstname-error' value={formErrorState.customerError.firstNameError} userState={userInputState.firstName} testId='pm-firstname-error' />
                          </FormFieldGroup>}
                        </Col>
                      )}
                      
                      {fieldVisibility.isLastName  && (
                        <Col xs={12} md={6} className="mb-24">
                        {isOverviewPage  && !isEditingCustomerInfo ? <div ><label className="form-label" data-testid="pm-lastName-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.LAST_NAME, defaultMessage: "Last name" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.customer.lastName}</span></div>
                          :<FormFieldGroup
                          id="lastName"
                          label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.LAST_NAME, defaultMessage: "Last name" })}
                          testId="pm-lastName-label"
                          >
                          <Form.Control
                            type="text"
                            value={state.customer.lastName}
                            onChange={(e) => {
                              dispatch({
                                type: 'UPDATE_CUSTOMER',
                                field: 'lastName',
                                value: e.target.value
                              })
                              setPaymentFormData({
                                ...PaymentFormData,
                                lastName:e.target.value
                              })
                              validateLastName(e.target.value, errorDispatch)
                            }}
                            onBlur={(e) => {
                              validateLastName(e.target.value, errorDispatch)
                              setUserInputState({...userInputState, lastName: ''})
                            }}
                            onFocus={() => setUserInputState({...userInputState, lastName: 'typing'})}
                            placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.LAST_NAME, defaultMessage: "Enter surname" })}
                            data-testid="pm-lastName-input"
                            className={formErrorState.customerError.lastNameError ? 'error' : ''}
                          />
                          <FormFieldError id='lastname-error' value={formErrorState.customerError.lastNameError} userState={userInputState.lastName} testId='pm-lastname-error' />
                          </FormFieldGroup>}
                        </Col>
                      )}
                    </Row>

                    <Row className="flex-grow-1">
                      {fieldVisibility.isDob && ( 
                        <Col xs={12} md={12} className="mb-24">
                      {isOverviewPage && !isEditingCustomerInfo ? <div ><label className="form-label" data-testid="pm-dob-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DATE_OF_BIRTH, defaultMessage: "Date of birth" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.customer.dob}</span></div>
                          :<FormFieldGroup
                            id="dob"
                            label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DATE_OF_BIRTH, defaultMessage: "Date of birth" })}
                            testId="pm-dob-label"
                          >
                            <Form.Control
                              type="text"
                              inputMode="numeric"
                              placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.DATE_OF_BIRTH, defaultMessage: "DD.MM.YYYY" })}
                              value={state.customer.dob}
                              maxLength={10}
                              onChange={(e) => {
                                dispatch({
                                  type: 'FORMAT_DOB',
                                  value: e.target.value.replace(/\D/g, '')
                                })
                                
                                validateDob(e.target.value, errorDispatch)
                              }}
                              onBlur={(e) => {
                                validateDob(e.target.value, errorDispatch)
                                setUserInputState({...userInputState, dob: ''})
                              }}
                              onFocus={() => setUserInputState({...userInputState, dob: 'typing'})}
                              data-testid="pm-dob-input"
                              className={formErrorState.customerError.dobError ? 'error' : ''}
                            />
                            <FormFieldError id='dob-error' value={formErrorState.customerError.dobError} userState={userInputState.dob} testId='pm-dob-error' />
                          </FormFieldGroup>}
                        </Col>
                      )}
                    </Row>
                  </div>
                )}

                {/* Contact Information Section */}
                {fieldVisibility.isLabelForContact && (
                  <div className="contact-information mt-2">
                    <Row className="mb-16">
                      <legend data-testid="pm-label-contact-info" className="section-title" style={{ display: isOverviewPage ? 'inline-flex' : 'block' }}>
                        {intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.SECTION_HEADINGS.CONTACT_INFORMATION, defaultMessage: "Contact Information" })}
                        {!!isOverviewPage && 
                        <span className="text">{!isEditingContactInfo ? <a href="#" onClick={(e)=>{ e.preventDefault(); setIsEditingContactInfo(true)}} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}><FormattedMessage id={CONSTANTS.FORM_EDIT.EDIT_LINK_TEXT} defaultMessage="Edit"/></a>:
                          <a href="#" onClick={(e)=>{
                            e.preventDefault(); 
                            validateContactInfo();
                          }} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}><FormattedMessage id={CONSTANTS.FORM_EDIT.SAVE_BUTTON_TEXT} defaultMessage="Save"/></a>}
                        </span>}
                      </legend>
                      {!isOverviewPage &&<span className="section-subtitle volte-medium" data-testid="pm-text-contact-info">
                        <FormattedMessage
                          id={CONSTANTS.PAYMENT_FORM.SECTION_HEADINGS.CONTACT_SUB_INFORMATION}
                          defaultMessage="Please fill in your contact information"
                        />
                      </span>}
                    </Row>
                    <Row>
                        {fieldVisibility.isEmail  && (
                          <Col xs={12} md={12} className="mb-24"> 
                        {isOverviewPage && !isEditingContactInfo ? <div ><label className="form-label" data-testid="pm-email-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.EMAIL_ADDRESS, defaultMessage: "Email address" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.contact.email}</span></div>
                          :<FormFieldGroup
                            id="email"
                            label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.EMAIL_ADDRESS, defaultMessage: "Email address" })}
                            testId="pm-email-label">
                            <Form.Control
                              type="email"
                              value={state.contact.email}
                              onChange={(e) => {
                                dispatch({
                                  type: 'UPDATE_CONTACT',
                                  field: 'email',
                                  value: e.target.value.replace(/\s/g, '')
                                });
                                setPaymentFormData({
                                  ...PaymentFormData,
                                  email:e.target.value.replace(/\s/g, '')
                                })
                                validateEmail(e.target.value, errorDispatch); 
                              }}
                              onBlur={(e) => {
                                validateEmail(e.target.value, errorDispatch);
                                setUserInputState({...userInputState, email: ''})
                              }}
                              onFocus={() => setUserInputState({...userInputState, email: 'typing'})}
                              placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.EMAIL_ADDRESS, defaultMessage: "Enter email address" })}
                              data-testid="pm-email-input"
                              className={formErrorState.contactError.emailError ? 'error' : ''}
                            />
                            <FormFieldError id='email-error' value={formErrorState.contactError.emailError} userState={userInputState.email} testId='pm-email-error' />
                            </FormFieldGroup>}
                          </Col>
                        )}
                      
                        {fieldVisibility.isPhone && (
                          <Col xs={12} md={12} className="mb-24">
                          {isOverviewPage && !isEditingContactInfo? <div ><label className="form-label" data-testid="pm-phone-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.PHONE_NUMBER, defaultMessage: "Phone number" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.contact.phone}</span></div>
                            :<FormFieldGroup
                              id="phone"
                              label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.PHONE_NUMBER, defaultMessage: "Phone number" })}
                              testId="pm-phone-label"
                            >
                            <Form.Control
                              type="tel"
                              value={state.contact.phone}
                              onChange={(e) =>{
                                dispatch({
                                  type: 'UPDATE_CONTACT',
                                  field: 'phone',
                                  value: e.target.value
                                })
                                setPaymentFormData({
                                  ...PaymentFormData,
                                  phone:e.target.value
                                })
                                validatePhoneNumber(e.target.value, errorDispatch);
                              }}
                              onBlur={(e) => {
                                validatePhoneNumber(e.target.value, errorDispatch);
                                setUserInputState({...userInputState, phone: ''})
                              }}
                              onFocus={() => setUserInputState({...userInputState, phone: 'typing'})}
                              placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.PHONE_NUMBER, defaultMessage: "Enter phone number" })}
                              data-testid="pm-phone-input"
                              className={formErrorState.contactError.phoneError ? 'error' : ''}
                            />
                            <FormFieldError id='phone-error' value={formErrorState.contactError.phoneError} userState={userInputState.phone} testId='pm-phone-error' />
                          </FormFieldGroup>}
                          </Col>
                        )}
                    </Row>
                  </div>
                )}

                {/* Billing Address Section */}
                {fieldVisibility.isLabelForBilling && (
                  <div className="billing-address mt-2">
                    <Row className="mb-16">
                      <legend data-testid="pm-label-billingaddress" className="section-title" style={{ display: isOverviewPage ? 'inline-flex' : 'block' }}>
                        {intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.SECTION_HEADINGS.BILLING_ADDRESS, defaultMessage: "Billing address" })}
                        {!!isOverviewPage && <span className="text">{!isEditingBillingInfo ? <a href="#" onClick={(e)=>{e.preventDefault(); setIsEditingBillinfInfo(true);}} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}>
                              <FormattedMessage id={CONSTANTS.FORM_EDIT.EDIT_LINK_TEXT} defaultMessage="Edit"/></a>:<a href="#" onClick={(e)=>{e.preventDefault(); validateBillingInfo();}} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}>
                              <FormattedMessage id={CONSTANTS.FORM_EDIT.SAVE_BUTTON_TEXT} defaultMessage="Save"/>
                        </a>}
                      </span>}
                      </legend>
                      {!isOverviewPage && <span className="section-subtitle volte-medium" data-testid="pm-text-contact-info">
                        <FormattedMessage
                          id={CONSTANTS.PAYMENT_FORM.SECTION_HEADINGS.BILLING_SUB_HEADING}
                          defaultMessage="Please fill in your contact information"
                        />
                      </span>}
                    </Row>

                    <Row>
                      {fieldVisibility.isStreet && (
                        <Col xs={12} md={12} className="mb-24">
                        {isOverviewPage && !isEditingBillingInfo  ? <div ><label className="form-label" data-testid="pm-streetaddress-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.STREET_NAME_AND_HOUSE_NUMBER, defaultMessage: "Street name and house number" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.billing.address}</span></div>:
                          <FormFieldGroup
                          id="streetAddress"
                          label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.STREET_NAME_AND_HOUSE_NUMBER, defaultMessage: "Street name and house number" })}
                          testId="pm-streetaddress-label"
                          >
                            <Form.Control
                              type="text"
                              value={state.billing.address}
                              onChange={(e) => {
                                dispatch({
                                  type: 'UPDATE_BILLING',
                                  field: 'address',
                                  value: e.target.value
                                })
                                setPaymentFormData({
                                  ...PaymentFormData,
                                  address:e.target.value
                                })
                                validateStreet(e.target.value, errorDispatch)
                              }}
                              onBlur={(e) => {
                                validateStreet(e.target.value, errorDispatch);
                                setUserInputState({...userInputState, address: ''})
                              }}
                              onFocus={() => setUserInputState({...userInputState, address: 'typing'})}
                              placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.STREET_NAME_AND_HOUSE_NUMBER, defaultMessage: "Enter street name and house number" })}
                              data-testid="pm-address-input"
                              className={formErrorState.billingError.addressError ? 'error' : ''}
                            />
                            <FormFieldError id='address-error' value={formErrorState.billingError.addressError} userState={userInputState.address} testId='pm-address-error' />
                          </FormFieldGroup>}
                        </Col>
                      )}
                    </Row>

                    <Row>
                      {fieldVisibility.isZip && (
                        <Col xs={12} md={6} className="mb-24">
                        {isOverviewPage && !isEditingBillingInfo  ? <div ><label className="form-label" data-testid="pm-zipcode-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.ZIP_CODE, defaultMessage: "Zip code" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.billing.zipcode}</span></div>:
                          <FormFieldGroup
                          id="zip-code"
                          label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.ZIP_CODE, defaultMessage: "Zip code" })}
                          testId="pm-zipcode-label"
                          >
                          <Form.Control
                            type="text"
                            value={state.billing.zipcode}
                            onChange={(e) => {
                              dispatch({
                                type: 'UPDATE_BILLING',
                                field: 'zipcode',
                                value: e.target.value
                              })
                              setPaymentFormData({
                                ...PaymentFormData,
                                zipcode:e.target.value
                              })
                              validateZip(e.target.value, errorDispatch)
                            }}
                            onBlur={(e) => {
                              validateZip(e.target.value, errorDispatch);
                              setUserInputState({...userInputState, zip: ''})
                            }}
                            onFocus={() => setUserInputState({...userInputState, zip: 'typing'})}
                            placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.ZIP_CODE, defaultMessage: "Enter zip code" })}
                            data-testid="pm-zip-code-input"
                            className={formErrorState.billingError.zipcodeError ? 'error' : ''}
                          />
                          <FormFieldError id='zipcode-error' value={formErrorState.billingError.zipcodeError} userState={userInputState.zip} testId='pm-zipcode-error' />
                        </FormFieldGroup>}
                        </Col>
                      )}
                      
                      {fieldVisibility.isCity && (
                        <Col xs={12} md={6} className="mb-24">
                        {isOverviewPage && !isEditingBillingInfo  ? <div ><label className="form-label" data-testid="pm-city-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.CITY, defaultMessage: "City" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.billing.city}</span></div>:
                          <FormFieldGroup
                          id="city"
                          label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.CITY, defaultMessage: "City" })}
                          testId="pm-city-label"
                          >
                            <Form.Control
                              type="text"
                              value={state.billing.city}
                              onChange={(e) => {
                                dispatch({
                                  type: 'UPDATE_BILLING',
                                  field: 'city',
                                  value: e.target.value
                                })
                                setPaymentFormData({
                                  ...PaymentFormData,
                                  city:e.target.value
                                })
                                validateCity(e.target.value, errorDispatch)
                              }}
                              onBlur={(e) => {
                                validateCity(e.target.value, errorDispatch);
                                setUserInputState({...userInputState, city: ''})
                              }}
                              onFocus={() => setUserInputState({...userInputState, city: 'typing'})}
                              placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.CITY, defaultMessage: "Enter city name" })}
                              data-testid="pm-city-input"
                              className={formErrorState.billingError.cityError ? 'error' : ''}
                            />
                            <FormFieldError id='city-error' value={formErrorState.billingError.cityError} userState={userInputState.city} testId='pm-city-error' />
                          </FormFieldGroup>}
                        </Col>
                      )}
                    </Row>

                    <Row className="flex-grow-1">
                        {fieldVisibility.isCountryCode && (
                            <Col xs={12} md={12} className="mb-24">
                            {isOverviewPage && !isEditingBillingInfo  ? <div ><label className="form-label" data-testid="pm-country-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.COUNTRY, defaultMessage: "Country" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.billing.country}</span></div>:
                              <FormFieldGroup
                              id="country"
                              label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.COUNTRY, defaultMessage: "Country" })}
                              testId="pm-country-label"
                              >
                              <CustomDropdown
                                id="country"
                                disable={false}
                                value={
                                  state.billing.country == "Austria" ? intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.AUSTRIA, defaultMessage: "Austria" })
                                    : intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.GERMANY, defaultMessage: "GERMANY" })
                                  }
                                options={[
                                  {
                                    key: "Austria",
                                    value: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.AUSTRIA, defaultMessage: "Austria" })}`
                                  },
                                  {
                                    key: "Germany",
                                    value: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.GERMANY, defaultMessage: "Germany" })}`,
                                  },
                                ]}
                                testId="pm-country-input"
                                onSelect={(value) =>
                                  dispatch({
                                    type: 'UPDATE_BILLING',
                                    field: 'country',
                                    value
                                  })
                                }
                              />
                              </FormFieldGroup>}
                            </Col>
                        )}
                    </Row>
                  </div>
                )}

                 {/* Payment Information Section */}
                <Row className="flex-grow-1">
                  {fieldVisibility.isIban && (
                    <Col xs={12} md={6} className="mb-24">
                    {isOverviewPage && !isEditingBillingInfo ? <div ><label className="form-label" data-testid="pm-iban-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.IBAN, defaultMessage: "IBAN" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.payment.iban}</span></div>:
                      <FormFieldGroup
                      id="iban"
                      label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.IBAN, defaultMessage: "IBAN" })}
                      testId="pm-iban-label"
                      >
                      <Form.Control
                        type="text"
                        value={state.payment.iban}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_PAYMENT',
                            field: 'iban',
                            value: e.target.value.replace(/[^\dA-Z]/g, '')
                          })
                          setPaymentFormData({
                            ...PaymentFormData,
                            iban:e.target.value.replace(/[^\dA-Z]/g, '')
                          })
                          validateIBAN(e.target.value, errorDispatch)
                        }}
                        onBlur={(e) => {
                          validateIBAN(e.target.value, errorDispatch);
                          setUserInputState({...userInputState, iban: ''})
                        }}
                        onFocus={() => setUserInputState({...userInputState, iban: 'typing'})}
                        onKeyDown={(e) => { if (e.key === " ") { e.preventDefault() } }}   //preventing user from inputing whitespaces
                        placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.IBAN, defaultMessage: "Enter IBAN" })}
                        data-testid="pm-iban-input"
                        className={formErrorState.paymentError.iban ? 'error' : ''}
                      />
                      <FormFieldError id='iban-error' value={formErrorState.paymentError.iban} userState={userInputState.iban} testId='pm-iban-error'/>
                      </FormFieldGroup>}
                    </Col>
                  )}
                
                  {fieldVisibility.isBankHolder && (
                    <Col xs={12} md={6} className="mb-24">
                    {isOverviewPage && !isEditingBillingInfo ? <div ><label className="form-label" data-testid="pm-bankaccholder-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.BANK_ACCOUNT_HOLDER, defaultMessage: "Bank account holder" })} <span style={{color:"#f50057"}}>*</span></label><span className='field-value-overview' >{state.payment.bankAccountHolder}</span></div>:
                      <FormFieldGroup
                      id="bankAccountHolder"
                      label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.BANK_ACCOUNT_HOLDER, defaultMessage: "Bank account holder" })}
                      testId="pm-bankaccholder-label"
                      >
                      <Form.Control
                        type="text"
                        value={state.payment.bankAccountHolder}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_PAYMENT',
                            field: 'bankAccountHolder',
                            value: e.target.value
                          })
                          setPaymentFormData({
                            ...PaymentFormData,
                            bankaccholder: e.target.value
                          })
                          validateAccountHolder(e.target.value, errorDispatch)
                        }}
                        onBlur={(e) => {
                          validateAccountHolder(e.target.value, errorDispatch);
                          setUserInputState({...userInputState, accountHolder: ''})
                        }}
                        onFocus={() => setUserInputState({...userInputState, accountHolder: 'typing'})}
                        placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.BANK_ACCOUNTHOLDER, defaultMessage: "Enter bank account holder" })}
                        data-testid="pm-bankaccholder-input"
                        className={formErrorState.paymentError.bankAccountHolderError ? 'error' : ''}
                      />
                      <FormFieldError id='accountHolderName-error' value={formErrorState.paymentError.bankAccountHolderError} userState={userInputState.accountHolder} testId='pm-accountHolderName-error'/>
                      </FormFieldGroup>}
                    </Col>
                  )}
                </Row>
              </Form>
            </Col>
          </Row>

          {/* Agreement Section */}
          <Row className="d-flex justify-content-center align-items-end mb-24 mt-2">
            <h5 data-testid="pm-label-terms-payment" className="section-title">
              {intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_SEC_TITLE, defaultMessage: "Terms of Payment" })}
            </h5>
            <div className="terms-checkbox mt-3" data-testid="pm-terms-checkbox">
              <Form.Check.Input
                type="checkbox"
                className="terms-checkbox-input m-0"
                checked={state.agreed}
                onChange={() => dispatch({ type: 'TOGGLE_AGREEMENT' })}
                data-testid="pm-terms-checkbox-input"
              />
              <label className="terms-checkbox-label" data-testid="pm-terms-checkbox-label">
                <FormattedMessage
                  id={CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_TEXT}
                  defaultMessage={CONSTANTS.PAYMENT_CHECKBOX_TEXT}
                />
              </label>
            </div>
            {state.agreed ? (
              (formErrorState.notAgreedError = "")
            ) : (
              <FormFieldError
                id='not-agree'
                value={formErrorState.notAgreedError}
                userState=''
                testId='pn-not-agree'
              />
            )}
            <div className="term_policy_text mt-3"
              dangerouslySetInnerHTML={{
                __html: intl.formatMessage({
                  id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_DESCRIPTION,
                  defaultMessage: CONSTANTS.PAYMENT_CHECKBOX_DESCRIPTION,
                }),
              }}
            >
            </div>
            <PaylaDcs snippetToken={snippetToken} agreed={state.agreed} />
          </Row>

          {/* Action Buttons */}
          <Row className="d-flex align-items-center">
            {showPaymentProcessing ? (
              <Col className={`align-self-start`}>
                <PaymentProcessingLoader />
              </Col>
            ) : (
              <Col className="text-end">
                 <button
                  className="btn btn-outline rounded-5 px-3 me-2 btn-invoice-outline"
                  onClick={handleOverviewPage}
                  data-testid="pm-overview-btn"
                >
                <FormattedMessage id={CONSTANTS.PAYMENT_FORM.BUTTON.GO_TO_OVERVIEW} defaultMessage="Go to overview"/>
                </button>
                <Button
                  variant="primary"
                  className={`btn btn-primary text-white px-4 rounded-4 volte-medium ${showPaymentProcessing ? 'transform' : ''}`}
                  onClick={handleSubmit}
                  data-testid="pm-submit-btn"
                >
                  <FormattedMessage id={CONSTANTS.PAYMENT_FORM.BUTTON.SUBMIT_PAYMENT} defaultMessage="Submit Payment" />
                </Button>
              </Col>
            )}
          </Row>
        </Container>
        : <PaymentNotSupported />
      }
    </>
  );
};

export default React.memo(DirectDebit);
